import { ExternalLink } from "@/components/ExternalLink";
import { Tables } from "@/gen/tableNames/tables";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { Clients } from "@/lib/clients";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type C3Transaction = {
  id: number;
  date: Date;
  c3_portfolio_client?: string;
  c3_portfolio_id?: number;
  c3_portfolio_name?: string;
  c3_static_datum_id?: number;
  c3_static_datum_name?: string;
  c3_portfolio_reporting_currency?: string;
  quantity?: number;
  market_value_ptf_ccy?: number;
  market_value_local_ccy?: number;
  local_currency?: string;
  category?: string;
  transfer?: string;
  description?: string;
  fees?: number;
  fee_currency?: string;
  fee_type?: string;
};

export const getC3TransactionColumns = (
  t: (key: string, options?: Record<string, unknown>) => string,
): ColumnDef<C3Transaction>[] => [
  {
    accessorKey: "id",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.id`, {
      ns: "tables",
      defaultValue: "ID",
    }),
  },
  {
    accessorKey: "date",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.date`, {
      ns: "tables",
      defaultValue: "Date",
    }),
    cell: ({ row }) => {
      return (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue("date")))}
        </div>
      );
    },
  },
  {
    accessorKey: "c3_portfolio_client",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.c3_portfolio_client`, {
      ns: "tables",
      defaultValue: "Client",
    }),
  },
  {
    accessorKey: "c3_portfolio_name",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.c3_portfolio_name`, {
      ns: "tables",
      defaultValue: "Account",
    }),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.C3}/${Tables.C3_PORTFOLIOS}/${row.original.c3_portfolio_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue("c3_portfolio_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "c3_static_datum_name",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.c3_static_datum_name`, {
      ns: "tables",
      defaultValue: "Asset",
    }),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/${Clients.C3}/${Tables.C3_STATIC_DATA}/${row.original.c3_static_datum_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue("c3_static_datum_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "quantity",
    header: () => (
      <div className="text-right">
        {t(`${Tables.C3_TRANSACTIONS}.columns.quantity`, {
          ns: "tables",
          defaultValue: "Quantity",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("quantity"))}
        </div>
      );
    },
  },
  {
    accessorKey: "market_value_ptf_ccy",
    header: () => (
      <div className="text-right">
        {t(`${Tables.C3_TRANSACTIONS}.columns.market_value_ptf_ccy`, {
          ns: "tables",
          defaultValue: "Market value (reporting ccy)",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value_ptf_ccy"))}
        </div>
      );
    },
  },
  {
    header: t(`${Tables.C3_TRANSACTIONS}.columns.c3_portfolio_reporting_currency`, {
      ns: "tables",
      defaultValue: "Portfolio reporting currency",
    }),
    accessorKey: "c3_portfolio_reporting_currency",
  },
  {
    accessorKey: "market_value_local_ccy",
    header: () => (
      <div className="text-right">
        {t(`${Tables.C3_TRANSACTIONS}.columns.market_value_local_ccy`, {
          ns: "tables",
          defaultValue: "Market value local ccy",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value_local_ccy"))}
        </div>
      );
    },
  },
  {
    accessorKey: "local_currency",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.local_currency`, {
      ns: "tables",
      defaultValue: "Local currency",
    }),
  },
  {
    accessorKey: "fees",
    header: () => (
      <div className="text-right">
        {t(`${Tables.C3_TRANSACTIONS}.columns.fees`, {
          ns: "tables",
          defaultValue: "Fees",
        })}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">{formatNumber(row.getValue("fees"))}</div>
      );
    },
  },
  {
    accessorKey: "fee_currency",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.fee_currency`, {
      ns: "tables",
      defaultValue: "Fee currency",
    }),
  },
  {
    accessorKey: "description",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.description`, {
      ns: "tables",
      defaultValue: "Description",
    }),
  },
  {
    accessorKey: "category",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.category`, {
      ns: "tables",
      defaultValue: "Movement type",
    }),
  },
  {
    accessorKey: "transfer",
    header: t(`${Tables.C3_TRANSACTIONS}.columns.transfer`, {
      ns: "tables",
      defaultValue: "Transfer type",
    }),
    cell: ({ row }) => {
      return <div className="text-left">{row.getValue("transfer")}</div>;
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
